<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Student Class Enrollment Management
            </h1>
            <div class="flex flex-wrap items-center gap-3">
                <button @click="openImportModal" class="btn btn-success">
                    Import
                </button>
                <button @click="openBulkModal" class="btn btn-primary">
                    Bulk Enrollment
                </button>
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Enrollment
                </button>
            </div>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by student name, class name, or semester..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Student Filter -->
                    <SearchableSelect
                        v-model="filters.student_id"
                        :options="studentOptions"
                        placeholder="All Students"
                        :option-label="getStudentLabel"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchStudentClasses"
                    />

                    <!-- Class Filter -->
                    <SearchableSelect
                        v-model="filters.class_id"
                        :options="classOptions"
                        placeholder="All Classes"
                        :option-label="getClassLabel"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchStudentClasses"
                    />

                    <!-- Semester Filter -->
                    <SearchableSelect
                        v-model="filters.semester_id"
                        :options="semesterOptions"
                        placeholder="All Semesters"
                        :option-label="getSemesterLabel"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchStudentClasses"
                    />

                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.is_active"
                        :options="statusOptions"
                        placeholder="All Status"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchStudentClasses"
                    />

                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="studentClassStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="7" />
        </div>

        <!-- Error State -->
        <div
            v-if="studentClassStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ studentClassStore.error }}
                    </p>
                    <button
                        @click="studentClassStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!studentClassStore.loading && !studentClassStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Student
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Class
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Semester
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Enrollment Date
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Duration
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="(
                                studentClass, index
                            ) in studentClassStore.studentClasses"
                            :key="
                                typeof studentClass === 'object'
                                    ? studentClass.id
                                    : index
                            "
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{
                                        studentClass?.student?.user?.name || "-"
                                    }}
                                    <div
                                        v-if="studentClass?.student?.student_id"
                                        class="text-xs text-gray-500 dark:text-gray-400"
                                    >
                                        ({{
                                            studentClass?.student?.student_id
                                        }})
                                    </div>
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ studentClass.class?.grade_level || "-" }}
                                -
                                {{ studentClass.class?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{
                                    studentClass.semester?.name &&
                                    studentClass.semester?.academic_year?.name
                                        ? `${studentClass.semester.name} - ${studentClass.semester.academic_year.name}`
                                        : studentClass.semester?.name || "-"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ formatDate(studentClass.enrollment_date) }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ studentClass.duration || "-" }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="studentClass.is_active"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Active
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200"
                                >
                                    Inactive
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editStudentClass(studentClass)"
                                    title="Edit Enrollment"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(studentClass)"
                                    title="Delete Enrollment"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="studentClassStore.studentClasses.length === 0"
                :icon="Users"
                title="No student class enrollments found"
                description="Get started by adding a new student class enrollment."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Enrollment
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="studentClassStore.meta"
                :per-page="studentClassStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- StudentClass Modal -->
        <StudentClassModal
            :showModal="showModal"
            :studentClass="selectedStudentClass"
            :isEditing="isEditing"
            @close="closeModal"
            @saved="handleSaved"
        />

        <!-- Bulk StudentClass Modal -->
        <BulkStudentClassModal
            :showModal="showBulkModal"
            @close="closeBulkModal"
            @saved="handleBulkSaved"
        />

        <!-- Import StudentClass Modal -->
        <StudentClassImportModal
            v-if="showImportModal"
            @close="closeImportModal"
            @success="handleImportSuccess"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="deleteModalTitle"
            :message="deleteModalMessage"
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useStudentClassStore } from "@/stores/studentClassStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import StudentClassModal from "@/components/StudentClassModal.vue";
import BulkStudentClassModal from "@/components/BulkStudentClassModal.vue";
import StudentClassImportModal from "@/components/StudentClassImportModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import NotificationService from "@/utils/notifications";
import studentApi from "@/api/student";
import classSchoollApi from "@/api/classSchooll";
import semesterApi from "@/api/semester";
import type { StudentClass } from "@/api/studentClass";
import type { Student } from "@/api/student";
import type { ClassSchooll } from "@/api/classSchooll";
import type { Semester } from "@/api/semester";
import { Plus, RotateCcw, Pencil, Trash2, Users } from "@lucide/vue";

const studentClassStore = useStudentClassStore();
// State
const searchQuery = ref("");
const filters = ref({
    student_id: "",
    class_id: "",
    semester_id: "",
    is_active: null as boolean | null,
});
const showModal = ref(false);
const showBulkModal = ref(false);
const showImportModal = ref(false);
const showDeleteModal = ref(false);
const selectedStudentClass = ref<StudentClass | null>(null);
const isEditing = ref(false);
const studentToDelete = ref<StudentClass | null>(null);
// Types for options with "Select All" option
type StudentOption =
    Student | { id: ""; user: { name: string }; student_id: string };
type ClassOption = ClassSchooll | { id: ""; grade_level: string; name: string };
type SemesterOption =
    Semester | { id: ""; name: string; academic_year: { name: string } };

const studentOptions = ref<StudentOption[]>([]);
const classOptions = ref<ClassOption[]>([]);
const semesterOptions = ref<SemesterOption[]>([]);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this student class enrollment? This action cannot be undone.",
);

// Computed

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.student_id !== "" ||
        filters.value.class_id !== "" ||
        filters.value.semester_id !== "" ||
        filters.value.is_active !== null ||
        studentClassStore.page !== 1 ||
        studentClassStore.perPage !== 15
    );
});

// Options for Status filter
const statusOptions = [
    { value: null, label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
];

// Methods
const handlePerPageChange = (value: number) => {
    studentClassStore.perPage = value;
    studentClassStore.page = 1;
    fetchStudentClasses();
};

const fetchStudentClasses = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await studentClassStore.fetchStudentClasses(searchFilters);
};

const fetchOptions = async () => {
    try {
        const [studentsResponse, classesResponse, semestersResponse] =
            await Promise.all([
                studentApi.getAll({ per_page: 1000 }),
                classSchoollApi.getAll({ per_page: 1000 }),
                semesterApi.getSemesters({ per_page: 1000 }),
            ]);
        // Add "Select All" option as the first element
        studentOptions.value = [
            { id: "", user: { name: "All Students" }, student_id: "" },
            ...(studentsResponse.data?.items || []),
        ];
        classOptions.value = [
            { id: "", grade_level: "", name: "All Classes" },
            ...(classesResponse.data?.items || []),
        ];
        semesterOptions.value = [
            { id: "", name: "All Semesters", academic_year: { name: "" } },
            ...(semestersResponse.data.items || []),
        ];
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const getStudentLabel = (student: StudentOption): string => {
    if (student.id === "") return student.user.name;
    return `${student.user?.name} (${student.student_id})`;
};

const getClassLabel = (classOption: ClassOption): string => {
    if (classOption.id === "") return classOption.name;
    return `${classOption.grade_level} - ${classOption.name}`;
};

const getSemesterLabel = (semester: SemesterOption): string => {
    if (semester.id === "") return semester.name;
    return `${semester.name} (${semester.academic_year?.name})`;
};

const debouncedSearch = useDebounce(fetchStudentClasses, 300);

const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
};

const openModal = () => {
    selectedStudentClass.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const openBulkModal = () => {
    showBulkModal.value = true;
};

const editStudentClass = (studentClass: StudentClass) => {
    selectedStudentClass.value = studentClass;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedStudentClass.value = null;
    isEditing.value = false;
};

const closeBulkModal = () => {
    showBulkModal.value = false;
};

const openImportModal = () => {
    showImportModal.value = true;
};

const closeImportModal = () => {
    showImportModal.value = false;
};

const handleImportSuccess = () => {
    showNotification("Student classes imported successfully", "success");
    fetchStudentClasses();
};

const confirmDelete = (studentClass: StudentClass) => {
    studentToDelete.value = studentClass;
    deleteModalTitle.value = "Delete Student Class Enrollment";
    deleteModalMessage.value = `Are you sure you want to delete the enrollment for "${studentClass.student?.user?.name}" in "${studentClass.class?.name}"? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    studentToDelete.value = null;
};

const handleSaved = () => {
    // Modal handles all API calls and errors
    // This handler just closes modal and refreshes data
    closeModal();
    fetchStudentClasses();
};

const handleBulkSaved = async (bulkData: any) => {
    try {
        // Check if the data is from import or bulk create
        if (Array.isArray(bulkData)) {
            // Import response - array of imported student classes
            const successMessage = `Successfully imported ${bulkData.length} student class enrollments`;
            showNotification(successMessage, "success");
            closeBulkModal();
        } else {
            // Bulk create response
            const result =
                await studentClassStore.bulkCreateStudentClasses(bulkData);

            if (result) {
                const successMessage = `Successfully enrolled ${result.success_count} out of ${result.total_count} students`;
                if (result.error_count > 0) {
                    showNotification(
                        `${successMessage}. ${result.error_count} students could not be enrolled.`,
                        "warning",
                    );
                } else {
                    showNotification(successMessage, "success");
                }
                closeBulkModal();
            } else {
                // Show error from store if bulk creation failed
                if (studentClassStore.error) {
                    showNotification(studentClassStore.error, "error");
                }
            }
        }
    } catch (error) {
        console.error("Failed to bulk enroll students:", error);
        showNotification("Failed to bulk enroll students", "error");
    }
};

const handleDelete = async () => {
    try {
        if (studentToDelete.value) {
            await studentClassStore.deleteStudentClass(
                studentToDelete.value.id,
            );
            closeDeleteModal();
        }
    } catch (error) {
        // Error handling is already in the store
        console.error("Failed to delete student class:", error);
    }
};

const goToPage = (page: number) => {
    studentClassStore.setPage(page);
    fetchStudentClasses();
};

const resetFilters = async () => {
    searchQuery.value = "";
    filters.value.student_id = "";
    filters.value.class_id = "";
    filters.value.semester_id = "";
    filters.value.is_active = null;
    await studentClassStore.resetFilters();
    fetchStudentClasses();
};

const showNotification = (
    message: string,
    type: "success" | "error" | "info" | "warning",
) => {
    switch (type) {
        case "success":
            NotificationService.success(message);
            break;
        case "error":
            NotificationService.error(message);
            break;
        case "info":
            NotificationService.info(message);
            break;
        case "warning":
            NotificationService.warning(message);
            break;
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchStudentClasses();
    fetchOptions();
});
</script>
